import type { Bookmarks } from '$lib/models/bookmarks';
import type { News, NewsBucket } from '$lib/models/news';
import type { Story, StoryContent } from '$lib/models/story';
import { DateTime } from 'luxon';
import { derived, get, writable, type Readable } from 'svelte/store';
import bookmarks from './bookmarks';
import settings from './settings';
import { logger } from '$lib/utils/logger';
import type { Request } from '$lib/models/request';

export interface NewsStore extends Readable<News>, Partial<News> {
  setNews: (news: News, newNews?: News) => void;
  addNews: (news: News, append?: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  taskWithLoading: (handler: () => void | Promise<void>) => Promise<void>;
  cacheForOfflineUse: (
    fetchContent: (url: string, fetchReadMoreContent: boolean) => Request<StoryContent>,
  ) => Promise<void>;
}

const initialState = { stories: [], isLoading: true };
const news = writable<News>(initialState);
const { update } = news;

function setNews(news: News, newNews?: News): void {
  if (!news && !newNews) {
    return;
  }

  const { stories, prevKey, nextKey } = news;
  const { stories: newStories = [], prevKey: newPrevKey } = newNews ?? {};
  const combinedStories = newStories.concat(stories);
  const deduplicatedStories = deduplicateStories(combinedStories);

  update((oldNews) => {
    return { ...oldNews, stories: deduplicatedStories, prevKey: newPrevKey ?? prevKey, nextKey };
  });
}

function addNews(news: News, append = true): void {
  if (!news) {
    return;
  }
  const { stories, prevKey, nextKey } = news;

  update((oldNews) => {
    const newStories = append ? oldNews.stories.concat(stories) : stories.concat(oldNews.stories);
    const newNews = { ...oldNews, stories: newStories };

    const deduplicatedStories = deduplicateStories(newStories);
    newNews.stories = deduplicatedStories;

    if (append) {
      newNews.nextKey = nextKey;
    } else if (prevKey) {
      newNews.prevKey = prevKey;
    }
    return newNews;
  });
}

function setIsLoading(isLoading: boolean): void {
  update((oldNews) => ({ ...oldNews, isLoading }));
}

async function taskWithLoading(handler: () => void | Promise<void>): Promise<void> {
  try {
    setIsLoading(true);
    await handler();
    setIsLoading(false);
  } catch (error) {
    const { name } = error as Error;
    if (name === 'AbortError') {
      return;
    }
    setIsLoading(false);
    logger.warn(error);
  }
}

async function cacheForOfflineUse(
  fetchContent: (url: string, fetchReadMoreContent: boolean) => Request<StoryContent>,
): Promise<void> {
  const fetchReadMoreContentPreference = get(settings).fetchReadMoreContent;
  const stories = get(news).stories.slice(0, 100);
  await Promise.allSettled(
    stories.map((story) => {
      const fetchReadMoreContent = fetchReadMoreContentPreference && story.source === 'news';
      return fetchContent(story.url, fetchReadMoreContent).request;
    }),
  );
}

function createStoryBuckets(stories: Array<Story>): Array<NewsBucket> | undefined {
  if (!stories) {
    return undefined;
  }

  const buckets: Map<string, NewsBucket> = new Map();
  function addToBucket(buckets: Map<string, NewsBucket>, story: Story): void {
    const timestamp = DateTime.fromISO(story.timestamp);
    const date = timestamp.toISODate() ?? '1970-01-01T00:00:00Z';
    if (buckets.has(date)) {
      buckets.get(date)?.stories.push(story);
    } else {
      const name = timestamp.setLocale('de-AT').toFormat('cccc, dd.MM.yyyy');
      const bucket = {
        name,
        date,
        stories: [story],
      };
      buckets.set(date, bucket);
    }
  }

  function compareBuckets(b1: NewsBucket, b2: NewsBucket): number {
    if (!b1.date || !b2.date) {
      return 0;
    }

    return b2.date.localeCompare(b1.date);
  }

  for (const story of stories) {
    addToBucket(buckets, story);
  }
  return Array.from(buckets.values()).sort(compareBuckets);
}

function setBookmarkStatus(stories: Array<Story>, bookmarkStories: Array<Story>): Array<Story> {
  const bookmarkIds = new Set(bookmarkStories.map((b) => b.id));
  return stories.map((story) => ({ ...story, isBookmarked: +bookmarkIds.has(story.id) }));
}

function deduplicateStories(stories: Array<Story>): Array<Story> {
  const storyIds = new Set<string>();
  return stories.filter((story) => {
    if (storyIds.has(story.id)) {
      logger.warnGroup('news-store', [['duplicate-story-id', story.id]]);
      return false;
    }

    storyIds.add(story.id);
    return true;
  });
}

let oldStories: Array<Story>;
let oldBookmarkStories: Array<Story>;
let cachedStories: Array<Story>;
let cachedStoryBuckets: Array<NewsBucket> | undefined;

function combineNewsAndBookmarks([news, bookmarks]: [News, Bookmarks]): News {
  const stories = news.stories;
  const bookmarkStories = bookmarks.stories;

  let newStories = cachedStories;
  let newStoryBuckets = cachedStoryBuckets;

  if (oldStories !== stories || oldBookmarkStories !== bookmarkStories) {
    oldStories = stories;
    oldBookmarkStories = bookmarkStories;

    newStories = setBookmarkStatus(stories, bookmarkStories);
    newStoryBuckets = createStoryBuckets(newStories);

    cachedStories = newStories;
    cachedStoryBuckets = newStoryBuckets;
  }

  return { ...news, stories: newStories, storyBuckets: newStoryBuckets };
}

const extendedStore = derived([news, bookmarks], combineNewsAndBookmarks);
const { subscribe } = extendedStore;

export default {
  subscribe,
  setNews,
  addNews,
  setIsLoading,
  taskWithLoading,
  cacheForOfflineUse,
} as NewsStore;
